import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Save, Wrench, Cpu, FileDown, History, RotateCcw, Trash2, Plus, X, Check, Cable, Zap } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { exportToolsToPDF } from "@/lib/pdfExport";
import { ToolsSignatureCanvas } from "./ToolsSignatureCanvas";
import { ToolReturnSignatureCanvas } from "./ToolReturnSignatureCanvas";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

interface EmployeeToolsFormProps {
  employeeId: string;
  employeeName?: string;
  readOnly?: boolean;
}

interface ToolDefinition {
  id: string;
  name: string;
  tool_type: string;
  requires_serial_number: boolean;
  requires_inventory_number: boolean;
  default_replacement_cost: number;
  sort_order: number;
  is_standard_copper: boolean;
  is_standard_fiber: boolean;
}

interface EmployeeTool {
  id?: string;
  tool_definition_id: string;
  is_assigned: boolean;
  serial_number: string;
  inventory_number: string;
  replacement_cost: number;
  assigned_date: string;
  notes: string;
  signature_url?: string | null;
  signed_at?: string | null;
  returned_at?: string | null;
  returned_signature_url?: string | null;
}

interface ToolsSignature {
  id: string;
  signature_url: string;
  signed_at: string;
  signed_by: string;
  tools_snapshot: any;
  signed_by_name?: string;
}

interface ToolReturn {
  id: string;
  tool_id: string;
  returned_at: string;
  signature_url: string;
  signed_by: string;
  notes?: string;
  tools_snapshot: any;
  signed_by_name?: string;
}

export function EmployeeToolsForm({ employeeId, employeeName = "Mitarbeiter", readOnly = false }: EmployeeToolsFormProps) {
  const { user, profile, userRole } = useAuth();
  const isEmployee = userRole === 'employee';
  const isAdmin = !readOnly && (userRole === 'admin' || userRole === 'hr_manager');
  const isToolsReadOnly = readOnly || isEmployee;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toolDefinitions, setToolDefinitions] = useState<ToolDefinition[]>([]);
  const [employeeTools, setEmployeeTools] = useState<Record<string, EmployeeTool>>({});
  
  // Signature state
  const [latestSignature, setLatestSignature] = useState<ToolsSignature | null>(null);
  const [signatureHistory, setSignatureHistory] = useState<ToolsSignature[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  // Return workflow state
  const [returnToolId, setReturnToolId] = useState<string | null>(null);
  const [returnHistory, setReturnHistory] = useState<ToolReturn[]>([]);
  const [showReturnHistory, setShowReturnHistory] = useState(false);

  // Add tool dialog state
  const [showAddToolDialog, setShowAddToolDialog] = useState(false);
  const [showMultiSelectDialog, setShowMultiSelectDialog] = useState(false);
  const [multiSelectType, setMultiSelectType] = useState<'hand_tool' | 'machine'>('hand_tool');
  const [selectedToolIds, setSelectedToolIds] = useState<Set<string>>(new Set());
  const [selectedToolType, setSelectedToolType] = useState<'hand_tool' | 'machine'>('hand_tool');
  const [newToolForm, setNewToolForm] = useState({
    name: "",
    tool_type: "hand_tool",
    requires_serial_number: false,
    requires_inventory_number: false,
    default_replacement_cost: 0,
    is_standard_copper: false,
    is_standard_fiber: false,
  });
  const [savingNewTool, setSavingNewTool] = useState(false);

  useEffect(() => {
    if (user && employeeId) {
      loadData();
    }
  }, [user, employeeId]);

  const loadData = async () => {
    try {
      const { data: definitions, error: defError } = await supabase
        .from("tool_definitions")
        .select("*")
        .order("sort_order");

      if (defError) throw defError;
      setToolDefinitions(definitions || []);

      const { data: empTools, error: toolsError } = await supabase
        .from("employee_tools")
        .select("*")
        .eq("employee_id", employeeId);

      if (toolsError) throw toolsError;

      const toolsMap: Record<string, EmployeeTool> = {};
      (empTools || []).forEach((tool: any) => {
        toolsMap[tool.tool_definition_id] = {
          id: tool.id,
          tool_definition_id: tool.tool_definition_id,
          is_assigned: tool.is_assigned,
          serial_number: tool.serial_number || "",
          inventory_number: tool.inventory_number || "",
          replacement_cost: tool.replacement_cost || 0,
          assigned_date: tool.assigned_date || "",
          notes: tool.notes || "",
          signature_url: tool.signature_url,
          signed_at: tool.signed_at,
          returned_at: tool.returned_at,
          returned_signature_url: tool.returned_signature_url,
        };
      });
      setEmployeeTools(toolsMap);

      const { data: signatures, error: sigError } = await supabase
        .from("employee_tools_signatures")
        .select("*")
        .eq("employee_id", employeeId)
        .order("signed_at", { ascending: false });

      if (sigError) throw sigError;

      if (signatures && signatures.length > 0) {
        const signerIds = [...new Set(signatures.map(s => s.signed_by))];
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", signerIds);

        const profileMap = new Map((profiles || []).map(p => [p.id, p.full_name]));

        const signaturesWithNames = signatures.map(s => ({
          ...s,
          signed_by_name: profileMap.get(s.signed_by) || "Unbekannt"
        }));

        setLatestSignature(signaturesWithNames[0]);
        setSignatureHistory(signaturesWithNames);
      }
    } catch (error) {
      console.error("Error loading tools data:", error);
      toast.error("Fehler beim Laden der Werkzeuge");
    } finally {
      setLoading(false);
    }
  };

  const handleToolChange = (toolId: string, field: keyof EmployeeTool, value: any) => {
    setEmployeeTools((prev) => ({
      ...prev,
      [toolId]: {
        ...prev[toolId],
        tool_definition_id: toolId,
        [field]: value,
        is_assigned: field === "is_assigned" ? value : prev[toolId]?.is_assigned ?? false,
      },
    }));
  };

  // Add a tool to the employee's assigned tools
  const handleAddTool = (toolId: string) => {
    const tool = toolDefinitions.find(t => t.id === toolId);
    if (!tool) return;

    setEmployeeTools((prev) => ({
      ...prev,
      [toolId]: {
        tool_definition_id: toolId,
        is_assigned: true,
        serial_number: "",
        inventory_number: "",
        replacement_cost: tool.default_replacement_cost,
        assigned_date: new Date().toISOString().split('T')[0],
        notes: "",
      },
    }));
  };

  // Add multiple tools at once
  const handleAddMultipleTools = (toolIds: string[]) => {
    const newTools: Record<string, EmployeeTool> = {};
    toolIds.forEach(toolId => {
      const tool = toolDefinitions.find(t => t.id === toolId);
      if (tool && !employeeTools[toolId]?.is_assigned) {
        newTools[toolId] = {
          tool_definition_id: toolId,
          is_assigned: true,
          serial_number: "",
          inventory_number: "",
          replacement_cost: tool.default_replacement_cost,
          assigned_date: new Date().toISOString().split('T')[0],
          notes: "",
        };
      }
    });
    
    setEmployeeTools(prev => ({ ...prev, ...newTools }));
    return Object.keys(newTools).length;
  };

  // Add standard tools for copper installers
  const handleAddStandardCopper = () => {
    const copperTools = toolDefinitions.filter(t => t.is_standard_copper && !employeeTools[t.id]?.is_assigned);
    const count = handleAddMultipleTools(copperTools.map(t => t.id));
    if (count > 0) {
      toast.success(`${count} Werkzeuge für Kupferinstallateure hinzugefügt`);
    } else {
      toast.info("Alle Standard-Werkzeuge sind bereits zugewiesen");
    }
  };

  // Add standard tools for fiber optic installers
  const handleAddStandardFiber = () => {
    const fiberTools = toolDefinitions.filter(t => t.is_standard_fiber && !employeeTools[t.id]?.is_assigned);
    const count = handleAddMultipleTools(fiberTools.map(t => t.id));
    if (count > 0) {
      toast.success(`${count} Werkzeuge für Glasfasermonteure hinzugefügt`);
    } else {
      toast.info("Alle Standard-Werkzeuge sind bereits zugewiesen");
    }
  };

  // Confirm multi-select
  const handleConfirmMultiSelect = () => {
    const count = handleAddMultipleTools(Array.from(selectedToolIds));
    if (count > 0) {
      toast.success(`${count} Werkzeuge hinzugefügt`);
    }
    setShowMultiSelectDialog(false);
    setSelectedToolIds(new Set());
  };

  // Toggle tool selection in multi-select
  const toggleToolSelection = (toolId: string) => {
    setSelectedToolIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(toolId)) {
        newSet.delete(toolId);
      } else {
        newSet.add(toolId);
      }
      return newSet;
    });
  };

  // Select all available tools in multi-select
  const selectAllAvailable = (toolType: 'hand_tool' | 'machine') => {
    const available = toolDefinitions.filter(
      t => t.tool_type === toolType && !employeeTools[t.id]?.is_assigned
    );
    setSelectedToolIds(new Set(available.map(t => t.id)));
  };

  // Remove a tool from the employee's assigned tools
  const handleRemoveTool = async (toolId: string) => {
    const empTool = employeeTools[toolId];
    
    // If tool exists in database, mark as not assigned
    if (empTool?.id) {
      setEmployeeTools((prev) => ({
        ...prev,
        [toolId]: {
          ...prev[toolId],
          is_assigned: false,
        },
      }));
    } else {
      // If tool was just added locally, remove it completely
      setEmployeeTools((prev) => {
        const newTools = { ...prev };
        delete newTools[toolId];
        return newTools;
      });
    }
  };

  // Create a new tool definition and add it to employee
  const handleCreateNewTool = async () => {
    if (!newToolForm.name.trim()) {
      toast.error("Bitte Namen eingeben");
      return;
    }

    setSavingNewTool(true);
    try {
      const maxOrder = Math.max(...toolDefinitions.map((t) => t.sort_order || 0), 0);
      
      const { data, error } = await supabase
        .from("tool_definitions")
        .insert([{
          name: newToolForm.name,
          tool_type: newToolForm.tool_type,
          requires_serial_number: newToolForm.requires_serial_number,
          requires_inventory_number: newToolForm.requires_inventory_number,
          default_replacement_cost: newToolForm.default_replacement_cost,
          sort_order: maxOrder + 1,
        }])
        .select()
        .single();

      if (error) throw error;

      // Add the new tool to definitions
      setToolDefinitions(prev => [...prev, data]);

      // Add it to employee's tools
      handleAddTool(data.id);

      toast.success(`"${data.name}" wurde hinzugefügt und zugewiesen`);
      setShowAddToolDialog(false);
      setNewToolForm({
        name: "",
        tool_type: "hand_tool",
        requires_serial_number: false,
        requires_inventory_number: false,
        default_replacement_cost: 0,
        is_standard_copper: false,
        is_standard_fiber: false,
      });
    } catch (error) {
      console.error("Error creating tool:", error);
      toast.error("Fehler beim Erstellen des Werkzeugs");
    } finally {
      setSavingNewTool(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: existing } = await supabase
        .from("employee_tools")
        .select("tool_definition_id")
        .eq("employee_id", employeeId);

      const existingIds = new Set((existing || []).map((e: any) => e.tool_definition_id));

      for (const [toolId, tool] of Object.entries(employeeTools)) {
        if (existingIds.has(toolId)) {
          const { error } = await supabase
            .from("employee_tools")
            .update({
              is_assigned: tool.is_assigned,
              serial_number: tool.serial_number || null,
              inventory_number: tool.inventory_number || null,
              replacement_cost: tool.replacement_cost || 0,
              assigned_date: tool.assigned_date || null,
              notes: tool.notes || null,
            })
            .eq("employee_id", employeeId)
            .eq("tool_definition_id", toolId);

          if (error) throw error;
        } else if (tool.is_assigned || tool.serial_number || tool.replacement_cost) {
          const { error } = await supabase.from("employee_tools").insert([
            {
              employee_id: employeeId,
              tool_definition_id: toolId,
              is_assigned: tool.is_assigned,
              serial_number: tool.serial_number || null,
              inventory_number: tool.inventory_number || null,
              replacement_cost: tool.replacement_cost || 0,
              assigned_date: tool.assigned_date || null,
              notes: tool.notes || null,
            },
          ]);

          if (error) throw error;
        }
      }

      toast.success("Werkzeuge gespeichert");
      await loadData();
    } catch (error) {
      console.error("Error saving tools:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleSignature = async (signatureDataUrl: string) => {
    try {
      const response = await fetch(signatureDataUrl);
      const blob = await response.blob();
      
      const filename = `${employeeId}/tools-signature-${Date.now()}.png`;
      
      const { error: uploadError } = await supabase.storage
        .from("employee-profiles")
        .upload(filename, blob, {
          contentType: "image/png",
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("employee-profiles")
        .getPublicUrl(filename);

      const publicUrl = urlData.publicUrl;
      const now = new Date().toISOString();

      const assignedTools = Object.entries(employeeTools)
        .filter(([_, tool]) => tool.is_assigned)
        .map(([id, tool]) => {
          const def = toolDefinitions.find(d => d.id === id);
          return {
            tool_definition_id: id,
            name: def?.name || "Unbekannt",
            serial_number: tool.serial_number,
            inventory_number: tool.inventory_number,
            replacement_cost: tool.replacement_cost,
            assigned_date: tool.assigned_date
          };
        });

      const { error: insertError } = await supabase
        .from("employee_tools_signatures")
        .insert({
          employee_id: employeeId,
          signature_url: publicUrl,
          signed_at: now,
          signed_by: employeeId,
          tools_snapshot: assignedTools
        });

      if (insertError) throw insertError;

      for (const [toolId, tool] of Object.entries(employeeTools)) {
        if (tool.is_assigned) {
          await supabase
            .from("employee_tools")
            .update({
              signature_url: publicUrl,
              signed_at: now,
              signed_by: employeeId
            })
            .eq("employee_id", employeeId)
            .eq("tool_definition_id", toolId);
        }
      }

      toast.success("Werkzeugempfang erfolgreich bestätigt");
      await loadData();
    } catch (error) {
      console.error("Error saving signature:", error);
      toast.error("Fehler beim Speichern der Unterschrift");
    }
  };

  const handleToolReturn = async (signatureDataUrl: string, notes: string) => {
    if (!returnToolId) return;
    
    try {
      const empTool = employeeTools[returnToolId];
      const toolDef = toolDefinitions.find(d => d.id === returnToolId);
      
      if (!empTool?.id) {
        toast.error("Werkzeug nicht gefunden");
        return;
      }

      const response = await fetch(signatureDataUrl);
      const blob = await response.blob();
      
      const filename = `${employeeId}/tool-return-${Date.now()}.png`;
      
      const { error: uploadError } = await supabase.storage
        .from("employee-profiles")
        .upload(filename, blob, {
          contentType: "image/png",
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("employee-profiles")
        .getPublicUrl(filename);

      const publicUrl = urlData.publicUrl;
      const now = new Date().toISOString();

      const toolSnapshot = {
        tool_definition_id: returnToolId,
        name: toolDef?.name || "Unbekannt",
        serial_number: empTool.serial_number,
        inventory_number: empTool.inventory_number,
        replacement_cost: empTool.replacement_cost,
        assigned_date: empTool.assigned_date
      };

      const { error: insertError } = await supabase
        .from("employee_tools_returns")
        .insert({
          employee_id: employeeId,
          tool_id: empTool.id,
          signature_url: publicUrl,
          signed_by: employeeId,
          notes: notes || null,
          tools_snapshot: toolSnapshot
        });

      if (insertError) throw insertError;

      const { error: updateError } = await supabase
        .from("employee_tools")
        .update({
          is_assigned: false,
          returned_at: now,
          returned_signature_url: publicUrl,
          returned_signed_by: employeeId,
          return_notes: notes || null
        })
        .eq("id", empTool.id);

      if (updateError) throw updateError;

      toast.success("Werkzeug-Rückgabe erfolgreich bestätigt");
      setReturnToolId(null);
      await loadData();
    } catch (error) {
      console.error("Error processing tool return:", error);
      toast.error("Fehler bei der Werkzeug-Rückgabe");
    }
  };

  const getUnsignedToolsCount = () => {
    if (!latestSignature) {
      return Object.values(employeeTools).filter(t => t.is_assigned).length;
    }

    const signedToolIds = new Set(
      (latestSignature.tools_snapshot || []).map((t: any) => t.tool_definition_id)
    );

    return Object.entries(employeeTools)
      .filter(([id, tool]) => tool.is_assigned && !signedToolIds.has(id))
      .length;
  };

  const handleDeleteSignature = async (signatureId: string) => {
    if (!isAdmin) return;
    
    try {
      const { error } = await supabase
        .from("employee_tools_signatures")
        .delete()
        .eq("id", signatureId);

      if (error) throw error;

      if (latestSignature?.id === signatureId) {
        const { error: updateError } = await supabase
          .from("employee_tools")
          .update({
            signature_url: null,
            signed_at: null,
            signed_by: null
          })
          .eq("employee_id", employeeId);

        if (updateError) throw updateError;
      }

      toast.success("Unterschrift gelöscht");
      await loadData();
    } catch (error) {
      console.error("Error deleting signature:", error);
      toast.error("Fehler beim Löschen der Unterschrift");
    }
  };

  const hasAssignedTools = Object.values(employeeTools).some(t => t.is_assigned);
  const unsignedToolsCount = getUnsignedToolsCount();
  const hasUnsignedTools = unsignedToolsCount > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Get assigned tools by type
  const assignedHandTools = Object.entries(employeeTools)
    .filter(([id, tool]) => {
      const def = toolDefinitions.find(d => d.id === id);
      return tool.is_assigned && def?.tool_type === "hand_tool";
    })
    .map(([id, tool]) => ({ id, tool, definition: toolDefinitions.find(d => d.id === id)! }));

  const assignedMachines = Object.entries(employeeTools)
    .filter(([id, tool]) => {
      const def = toolDefinitions.find(d => d.id === id);
      return tool.is_assigned && def?.tool_type === "machine";
    })
    .map(([id, tool]) => ({ id, tool, definition: toolDefinitions.find(d => d.id === id)! }));

  // Get available tools (not assigned to this employee)
  const availableHandTools = toolDefinitions.filter(
    t => t.tool_type === "hand_tool" && !employeeTools[t.id]?.is_assigned
  );
  const availableMachines = toolDefinitions.filter(
    t => t.tool_type === "machine" && !employeeTools[t.id]?.is_assigned
  );

  const renderToolCard = (
    id: string, 
    tool: EmployeeTool, 
    definition: ToolDefinition
  ) => {
    const isNewSinceSignature = latestSignature && tool.is_assigned && 
      !(latestSignature.tools_snapshot || []).some((t: any) => t.tool_definition_id === id);
    const isBeingReturned = returnToolId === id;

    if (isBeingReturned) {
      return (
        <div key={id} className="col-span-full">
          <ToolReturnSignatureCanvas
            onSave={handleToolReturn}
            onCancel={() => setReturnToolId(null)}
            toolName={definition.name}
            serialNumber={tool.serial_number}
          />
        </div>
      );
    }

    return (
      <div
        key={id}
        className={`flex flex-col p-3 border rounded-lg bg-card hover:bg-muted/50 transition-colors ${
          isNewSinceSignature ? "border-amber-500/50 bg-amber-500/5" : ""
        } ${tool.returned_at ? "border-blue-500/30 bg-blue-500/5" : ""}`}
      >
        {/* Tool name and remove button */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="font-medium text-sm">{definition.name}</span>
          {!isToolsReadOnly && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
              onClick={() => handleRemoveTool(id)}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-1 mb-2">
          {definition.requires_serial_number && (
            <Badge variant="outline" className="text-xs">S/N</Badge>
          )}
          {definition.requires_inventory_number && (
            <Badge variant="outline" className="text-xs">Inv.-Nr.</Badge>
          )}
          {isNewSinceSignature && (
            <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">Neu</Badge>
          )}
          {tool.returned_at && (
            <Badge variant="outline" className="text-xs border-blue-500 text-blue-600">
              Zurück {new Date(tool.returned_at).toLocaleDateString("de-DE")}
            </Badge>
          )}
        </div>

        {/* Price */}
        <div className="mb-2">
          <Label className="text-xs text-muted-foreground">Kosten (€)</Label>
          {isToolsReadOnly ? (
            <div className="h-7 text-sm flex items-center font-medium">
              {(tool.replacement_cost || definition.default_replacement_cost || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </div>
          ) : (
            <Input
              type="number"
              step="0.01"
              value={tool.replacement_cost || ""}
              onChange={(e) =>
                handleToolChange(id, "replacement_cost", parseFloat(e.target.value) || 0)
              }
              placeholder={definition.default_replacement_cost.toString()}
              className="h-7 text-sm"
            />
          )}
        </div>

        {/* Serial number if required */}
        {definition.requires_serial_number && (
          <div className="mb-2">
            <Label className="text-xs text-muted-foreground">Seriennummer</Label>
            {isToolsReadOnly ? (
              <div className="h-7 text-sm flex items-center">{tool.serial_number || "-"}</div>
            ) : (
              <Input
                value={tool.serial_number}
                onChange={(e) => handleToolChange(id, "serial_number", e.target.value)}
                placeholder="S/N eingeben"
                className="h-7 text-sm"
              />
            )}
          </div>
        )}

        {/* Inventory number if required */}
        {definition.requires_inventory_number && (
          <div className="mb-2">
            <Label className="text-xs text-muted-foreground">Inventarnummer</Label>
            {isToolsReadOnly ? (
              <div className="h-7 text-sm flex items-center">{tool.inventory_number || "-"}</div>
            ) : (
              <Input
                value={tool.inventory_number}
                onChange={(e) => handleToolChange(id, "inventory_number", e.target.value)}
                placeholder="Inv.-Nr. eingeben"
                className="h-7 text-sm"
              />
            )}
          </div>
        )}

        {/* Return button */}
        {!isToolsReadOnly && tool.is_assigned && tool.id && !tool.returned_at && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs border-blue-500/50 text-blue-600 hover:bg-blue-500/10 mt-auto"
            onClick={() => setReturnToolId(id)}
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Rückgabe
          </Button>
        )}
      </div>
    );
  };

  const renderToolSection = (
    assignedTools: Array<{ id: string; tool: EmployeeTool; definition: ToolDefinition }>,
    availableTools: ToolDefinition[],
    title: string,
    icon: React.ReactNode,
    toolType: 'hand_tool' | 'machine'
  ) => {
    // Count standard tools available
    const standardCopperCount = availableTools.filter(t => t.is_standard_copper).length;
    const standardFiberCount = availableTools.filter(t => t.is_standard_fiber).length;

    return (
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">{icon}</div>
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              <CardDescription>
                {assignedTools.length} {assignedTools.length === 1 ? "Werkzeug zugewiesen" : "Werkzeuge zugewiesen"}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Quick actions */}
          {!isToolsReadOnly && (
            <div className="space-y-3 mb-4">
              {/* Standard set buttons */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddStandardCopper}
                  disabled={standardCopperCount === 0}
                  className="border-orange-500/50 text-orange-600 hover:bg-orange-500/10"
                >
                  <Cable className="h-4 w-4 mr-2" />
                  Standard Kupfer ({standardCopperCount})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddStandardFiber}
                  disabled={standardFiberCount === 0}
                  className="border-blue-500/50 text-blue-600 hover:bg-blue-500/10"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Standard Glasfaser ({standardFiberCount})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setMultiSelectType(toolType);
                    setSelectedToolIds(new Set());
                    setShowMultiSelectDialog(true);
                  }}
                  disabled={availableTools.length === 0}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Mehrfachauswahl ({availableTools.length})
                </Button>
              </div>

              {/* Single tool dropdown */}
              <Select
                value=""
                onValueChange={(value) => {
                  if (value === "__new__") {
                    setNewToolForm(prev => ({ ...prev, tool_type: toolType }));
                    setSelectedToolType(toolType);
                    setShowAddToolDialog(true);
                  } else {
                    handleAddTool(value);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={`${toolType === 'hand_tool' ? 'Einzelnes Handwerkzeug' : 'Einzelne Maschine'} hinzufügen...`} />
                </SelectTrigger>
                <SelectContent>
                  {availableTools.map((tool) => (
                    <SelectItem key={tool.id} value={tool.id}>
                      <span className="flex items-center gap-2">
                        {tool.name}
                        {tool.is_standard_copper && <span className="w-2 h-2 rounded-full bg-orange-500" />}
                        {tool.is_standard_fiber && <span className="w-2 h-2 rounded-full bg-blue-500" />}
                      </span>
                    </SelectItem>
                  ))}
                  <SelectItem value="__new__" className="text-primary font-medium">
                    <span className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Neues {toolType === 'hand_tool' ? 'Handwerkzeug' : 'Gerät'} anlegen...
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Assigned tools grid */}
          {assignedTools.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2">
              {assignedTools.map(({ id, tool, definition }) => 
                renderToolCard(id, tool, definition)
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-4">
              Keine {title} zugewiesen
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  const handleExportPDF = () => {
    const toolsForExport = toolDefinitions.map(def => ({
      name: def.name,
      is_assigned: employeeTools[def.id]?.is_assigned || false,
      serial_number: employeeTools[def.id]?.serial_number,
      replacement_cost: employeeTools[def.id]?.replacement_cost || def.default_replacement_cost,
      assigned_date: employeeTools[def.id]?.assigned_date,
      tool_type: def.tool_type,
    }));
    exportToolsToPDF(employeeName, toolsForExport, latestSignature?.signature_url, latestSignature?.signed_at, latestSignature?.signed_by_name);
  };

  return (
    <div className="space-y-6">
      {renderToolSection(
        assignedHandTools, 
        availableHandTools, 
        "Handwerkzeuge", 
        <Wrench className="h-5 w-5 text-primary" />,
        "hand_tool"
      )}
      {renderToolSection(
        assignedMachines, 
        availableMachines, 
        "Maschinen & Messgeräte", 
        <Cpu className="h-5 w-5 text-primary" />,
        "machine"
      )}

      <div className="flex justify-end gap-2">
        {signatureHistory.length > 0 && (
          <Button variant="outline" onClick={() => setShowHistory(true)}>
            <History className="mr-2 h-4 w-4" />
            Unterschriften-Historie ({signatureHistory.length})
          </Button>
        )}
        <Button variant="outline" onClick={handleExportPDF}>
          <FileDown className="mr-2 h-4 w-4" />
          PDF exportieren
        </Button>
        {!isToolsReadOnly && (
          <Button onClick={handleSave} disabled={saving} size="lg">
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Alle Werkzeuge speichern
          </Button>
        )}
      </div>

      {/* Signature Section */}
      {hasAssignedTools && !isToolsReadOnly && (
        <div className="pt-4 border-t">
          <ToolsSignatureCanvas
            onSave={handleSignature}
            onDelete={latestSignature ? () => handleDeleteSignature(latestSignature.id) : undefined}
            existingSignature={latestSignature?.signature_url}
            signedAt={latestSignature?.signed_at}
            signedByName={latestSignature?.signed_by_name}
            hasUnsignedTools={hasUnsignedTools}
            unsignedToolsCount={unsignedToolsCount}
            isAdmin={isAdmin}
            disabled={false}
          />
        </div>
      )}

      {/* Add New Tool Dialog */}
      <Dialog open={showAddToolDialog} onOpenChange={setShowAddToolDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Neues {selectedToolType === 'hand_tool' ? 'Handwerkzeug' : 'Gerät/Maschine'} anlegen
            </DialogTitle>
            <DialogDescription>
              Das neue Werkzeug wird in den Einstellungen gespeichert und direkt diesem Mitarbeiter zugewiesen.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <div>
              <Label>Name</Label>
              <Input
                value={newToolForm.name}
                onChange={(e) => setNewToolForm({ ...newToolForm, name: e.target.value })}
                placeholder="z.B. Akkuschrauber Bosch"
              />
            </div>
            <div>
              <Label>Typ</Label>
              <Select
                value={newToolForm.tool_type}
                onValueChange={(value) => setNewToolForm({ ...newToolForm, tool_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hand_tool">Handwerkzeug</SelectItem>
                  <SelectItem value="machine">Maschine/Messgerät</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Standardkosten (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={newToolForm.default_replacement_cost}
                onChange={(e) =>
                  setNewToolForm({
                    ...newToolForm,
                    default_replacement_cost: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Seriennummer erforderlich</Label>
              <Switch
                checked={newToolForm.requires_serial_number}
                onCheckedChange={(checked) =>
                  setNewToolForm({ ...newToolForm, requires_serial_number: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Inventarnummer erforderlich</Label>
              <Switch
                checked={newToolForm.requires_inventory_number}
                onCheckedChange={(checked) =>
                  setNewToolForm({ ...newToolForm, requires_inventory_number: checked })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddToolDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreateNewTool} disabled={savingNewTool}>
              {savingNewTool && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Anlegen & Zuweisen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Multi-Select Dialog */}
      <Dialog open={showMultiSelectDialog} onOpenChange={setShowMultiSelectDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Mehrere {multiSelectType === 'hand_tool' ? 'Handwerkzeuge' : 'Maschinen'} auswählen
            </DialogTitle>
            <DialogDescription>
              Wählen Sie die Werkzeuge aus, die diesem Mitarbeiter zugewiesen werden sollen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {selectedToolIds.size} ausgewählt
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => selectAllAvailable(multiSelectType)}
              >
                Alle auswählen
              </Button>
            </div>

            <ScrollArea className="h-[400px] border rounded-lg p-2">
              <div className="space-y-1">
                {toolDefinitions
                  .filter(t => t.tool_type === multiSelectType && !employeeTools[t.id]?.is_assigned)
                  .map((tool) => (
                    <div
                      key={tool.id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors ${
                        selectedToolIds.has(tool.id) ? 'bg-primary/10' : ''
                      }`}
                      onClick={() => toggleToolSelection(tool.id)}
                    >
                      <Checkbox
                        checked={selectedToolIds.has(tool.id)}
                        onCheckedChange={() => toggleToolSelection(tool.id)}
                      />
                      <span className="flex-1">{tool.name}</span>
                      <div className="flex gap-1">
                        {tool.is_standard_copper && (
                          <span className="w-3 h-3 rounded-full bg-orange-500" title="Standard Kupfer" />
                        )}
                        {tool.is_standard_fiber && (
                          <span className="w-3 h-3 rounded-full bg-blue-500" title="Standard Glasfaser" />
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMultiSelectDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleConfirmMultiSelect} disabled={selectedToolIds.size === 0}>
              {selectedToolIds.size} Werkzeuge hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Unterschriften-Historie
            </DialogTitle>
            <DialogDescription>
              Alle Unterschriften zur Werkzeugübergabe für {employeeName}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 pr-4">
              {signatureHistory.map((sig, index) => (
                <Card key={sig.id} className={index === 0 ? "border-green-500/30" : ""}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-sm font-medium">
                          {new Date(sig.signed_at).toLocaleDateString("de-DE", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })} Uhr
                        </CardTitle>
                        <CardDescription className="text-xs">
                          Unterzeichnet von {sig.signed_by_name}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        {index === 0 && (
                          <Badge variant="outline" className="border-green-500 text-green-600">
                            Aktuell
                          </Badge>
                        )}
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteSignature(sig.id)}
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="border rounded-lg bg-white p-2">
                        <img 
                          src={sig.signature_url} 
                          alt="Unterschrift" 
                          className="max-h-[100px] w-auto mx-auto"
                        />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          Übergabe Werkzeuge ({(sig.tools_snapshot || []).length}):
                        </p>
                        <ul className="text-xs space-y-1">
                          {(sig.tools_snapshot || []).slice(0, 5).map((tool: any, i: number) => (
                            <li key={i} className="flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                              {tool.name}
                              {tool.serial_number && (
                                <span className="text-muted-foreground">({tool.serial_number})</span>
                              )}
                            </li>
                          ))}
                          {(sig.tools_snapshot || []).length > 5 && (
                            <li className="text-muted-foreground">
                              ... und {(sig.tools_snapshot || []).length - 5} weitere
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
